import { useEffect, useRef } from "react";
import { Box, styled } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";

const TabsRoot = styled(Box, {
  shouldForwardProp: (p) => p !== "tabVariant",
})(({ theme, tabVariant }) => ({
  display: "flex",
  alignItems: "center",
  // Tab counts grow — the users module has 7 today with 3 more commented out.
  // Scroll instead of wrapping or pushing the page sideways on narrow screens.
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },

  ...(tabVariant === "pill"
    ? {
        backgroundColor: theme.palette.divider,
        borderRadius: 999,
        padding: 4,
        gap: 2,
        width: "fit-content",
        maxWidth: "100%",
      }
    : {
        gap: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
}));

const TabLink = styled(NavLink, {
  shouldForwardProp: (p) => p !== "tabVariant",
})(({ theme, tabVariant }) => ({
  textDecoration: "none",
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "all 150ms ease",
  color: theme.palette.text.secondary,

  ...(tabVariant === "pill"
    ? {
        padding: "5px 20px",
        borderRadius: 999,
        fontSize: "0.875rem",
        "&:hover": { color: theme.palette.text.primary },
        "&.active": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }
    : {
        paddingBottom: theme.spacing(1),
        fontSize: "0.95rem",
        borderBottom: "2px solid transparent",
        "&:hover": { color: theme.palette.primary.main },
        "&.active": {
          color: theme.palette.primary.main,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          fontWeight: 600,
        },
      }),
}));

/**
 * URL-driven tabs. The router is the source of truth — there is no `value` to
 * keep in sync, so a tab can never disagree with the address bar.
 *
 * tabs: [{ name, path, end? }] — `path` is absolute.
 * variant: "underline" (page/entity sub-nav) | "pill" (module sections).
 * `end` matches the path exactly; without it a tab stays active on child routes,
 * which is usually what you want.
 */
const CustomTabs = ({ tabs = [], variant = "underline", sx }) => {
  const scrollRef = useRef(null);
  const { pathname } = useLocation();

  // Once tabs overflow, the active one can start off-screen. Bring it into
  // view on mount and whenever the route changes.
  useEffect(() => {
    const active = scrollRef.current?.querySelector("a.active");
    active?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);

  return (
    <TabsRoot ref={scrollRef} tabVariant={variant} sx={sx} role="tablist">
      {tabs.map((tab) => (
        <TabLink
          key={tab.path}
          to={tab.path}
          end={tab.end}
          tabVariant={variant}
          role="tab"
        >
          {tab.name}
        </TabLink>
      ))}
    </TabsRoot>
  );
};

export default CustomTabs;